import React, { useState } from "react";
import axios from "../../api/axios";
import { Modal, Button, Form } from "react-bootstrap";
import { toast } from "react-toastify";
import { PaginationPage } from "../../components/PaginationPage";
import Footer from "../../components/Footer";

function InstallmentSearchPage({ user, onLogout }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(false);

  const [selectedInstallment, setSelectedInstallment] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [showModal, setShowModal] = useState(false);

  // -----------------------------
  //  API
  // -----------------------------
  const searchInstallments = async (page=1 ) => {
    if (!query.trim()) return;

    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`/api/installmentsall/search`, {
        params: { query, page },
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log("installments...", res.data.data, res.data);
      setResults(res.data.data || []);
      setPagination({
        current_page: res.data.current_page,
        last_page: res.data.last_page,
      });
    } catch (err) {
      console.log(err);
    }

    setLoading(false);
  };

  // -----------------------------
  //  Pay Now Modal
  // -----------------------------
  const openPayModal = (installment) => {
    setSelectedInstallment(installment);
    setPayAmount(installment.due_amount); //

    setShowModal(true);
  };

  const downloadReceipt = async (receiptId, invoiceNumber) => {
    if (!receiptId) return;

    try {
      const res = await axios.get(`/api/invoices/${receiptId}/pdf`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Receipt-${invoiceNumber || receiptId}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.log(err);
      toast.error("Unable to download receipt.");
    }
  };

  const getReceiptFromInstallment = (ins) => {
    const paymentWithInvoice = [...(ins.payments || [])]
      .filter((p) => p.invoice_id)
      .sort((a, b) => b.id - a.id)[0];

    if (paymentWithInvoice?.invoice_id) {
      return {
        id: paymentWithInvoice.invoice_id,
        invoice_number: ins.invoice?.invoice_number,
      };
    }
    if (ins.invoice_id) {
      return {
        id: ins.invoice_id,
        invoice_number: ins.invoice?.invoice_number,
      };
    }
    return null;
  };

  // -----------------------------
  // Pay Now API Request
  // -----------------------------
  const payNow = async () => {
    if (!selectedInstallment) return;

    try {
      const res = await axios.post(
        `/api/installments/${selectedInstallment.booking_id}/${selectedInstallment.id}/pay`,
        { amount: payAmount }
      );
      console.log("pay amount", payAmount);
      toast.success("Payment Successful!");

      if (res.data?.receipt?.id) {
        await downloadReceipt(
          res.data.receipt.id,
          res.data.receipt.invoice_number
        );
      }

      searchInstallments(pagination.current_page);
      setShowModal(false);
    } catch (err) {
      console.log(err);
      alert("payment failed!");
    }
  };

  return (
    <>
      <div className="container-fluid">
        {/* ----------- Search Box ---------------- */}
        <h3 className="mb-3 text-center text-primary">Search</h3>

        <div className="input-group mb-3">
          <input
            type="text"
            placeholder="User | Property | Unit | Plot | Invoice | Installment No"
            className="form-control"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button className="btn btn-primary" onClick={searchInstallments}>
            Search
          </button>
        </div>

        {/* ----------- Loading Spinner ---------------- */}
        {loading && <p>Loading...</p>}

        {/* ----------- Results Table ---------------- */}
        {results.length > 0 && (
          <table className="table table-bordered mt-3">
            <thead>
              <tr>
                <th>Installment No</th>
                <th>Customer</th>
                <th>Property</th>
                <th>Unit</th>
                <th>Amount</th>
                <th>Due Amount</th>
                <th>Due Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {results.map((ins) => {
                const receipt = getReceiptFromInstallment(ins);
                return (
                <tr key={ins.id}>
                  <td>{ins.installment_number}</td>
                  <td>{ins.customer?.name}</td>
                  <td>{ins.property?.name}</td>
                  <td>{ins.unit?.unit_number}</td>
                  <td>{ins.installment_amount}</td>
                  <td>{ins.due_amount}</td>
                  <td>{ins.due_date?.split("T")[0]}</td>
                  <td>
                    {ins.status === "paid" ? (
                      <span className="badge bg-success">Paid</span>
                    ) : (
                      <span className="badge bg-warning">Pending</span>
                    )}
                  </td>

                  <td className="text-nowrap">
                    {ins.status !== "paid" && (
                      <button
                        className="btn btn-success btn-sm me-2"
                        onClick={() => openPayModal(ins)}
                      >
                        Pay Now
                      </button>
                    )}
                    {receipt && (
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() =>
                          downloadReceipt(receipt.id, receipt.invoice_number)
                        }
                      >
                        Receipt
                      </button>
                    )}
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
        )}

        {/* ----------- Payment Modal ---------------- */}
        <Modal show={showModal} onHide={() => setShowModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Installment Payment</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <p>
              <strong>Installment:</strong>{" "}
              {selectedInstallment?.installment_number}
            </p>

            <Form.Group>
              <Form.Label>Amount</Form.Label>
              <Form.Control
                type="number"
                value={payAmount}
                onChange={(e) => setPayAmount(e.target.value)}
              />
            </Form.Group>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Close
            </Button>
            <Button variant="primary" onClick={payNow}>
              Pay
            </Button>
          </Modal.Footer>
        </Modal>
        <div></div>
      </div>
      <PaginationPage
        pagination={pagination}
        onPageChange={(page) => searchInstallments(page)}
      />
    </>
  );
}

export default InstallmentSearchPage;
